import { ReactNode, createContext, useCallback, useContext, useMemo } from 'react'

import { FormsAnswerFragment } from 'generated/resources/graphql'

export type FormMembership = {
  id: string
  formId: string
  answers: FormsAnswerFragment[]
  score?: number | null
}

type FormMembershipContextValue = {
  getMembershipByFormId: (formId: string) => FormMembership | undefined
}

const FormMembershipContext = createContext<FormMembershipContextValue | null>(null)

interface FormMembershipProviderProps {
  memberships: FormMembership[]
  children: ReactNode
}

export function FormMembershipProvider({ memberships, children }: FormMembershipProviderProps) {
  const membershipMap = useMemo(() => {
    const map = new Map<string, FormMembership>()
    memberships.forEach((membership) => {
      if (membership.formId) {
        map.set(membership.formId, membership)
      }
    })
    return map
  }, [memberships])

  const getMembershipByFormId = useCallback(
    (formId: string) => membershipMap.get(formId),
    [membershipMap]
  )

  const contextValue = useMemo(() => ({ getMembershipByFormId }), [getMembershipByFormId])

  return (
    <FormMembershipContext.Provider value={contextValue}>{children}</FormMembershipContext.Provider>
  )
}

export function useFormMemberships() {
  const context = useContext(FormMembershipContext)
  if (!context) {
    throw new Error('useFormMemberships must be used within FormMembershipProvider')
  }
  return context
}
